/**
 * Door to Door page copy, from the SEO team's "saharicargo content" brief.
 */
import { FaPlane, FaShip } from 'react-icons/fa';
import heroImage from '../../../assets/images/testimonials-bg.webp';
import { CANNOT_SHIP } from './shared';
import type { ServiceContent } from './types';

const doorToDoor: ServiceContent = {
  slug: 'door-to-door',
  breadcrumbName: 'Door to Door Cargo',
  meta: {
    title: 'Door to Door Cargo & Freight Service | Sahari Cargo',
    description:
      "International door-to-door cargo delivery by air and sea. We collect from your address and deliver to the recipient's — no drop-off points. Get a quote.",
  },
  heroImage,
  heroAlt: 'Door to door cargo delivery',
  h1: 'Door to Door Cargo & Freight Service',
  intro: [
    "Most cargo bookings still end at a warehouse or a collection counter, which means someone on the other end has to go pick it up, clear customs themselves, or wait around for a call that never quite comes on time. Door-to-door cargo delivery skips that step entirely. We collect your shipment from your address in Saudi Arabia and hand it directly to the recipient, wherever in the world that happens to be — with everything in between, customs paperwork included, handled by us.",
  ],
  sections: [
    {
      heading: 'What Door-to-Door Cargo Delivery Actually Means',
      paragraphs: [
        '"Door to door" gets used loosely in this industry, so here\'s what it means with us specifically: one booking covers pickup, export clearance, the international leg, import clearance at the destination, and final delivery to the recipient\'s address. You\'re not coordinating a local courier, a freight forwarder, and a delivery company in the destination country separately — it\'s one shipment, one tracking number, one point of contact if something needs checking.',
      ],
    },
    {
      heading: 'Door to Door Freight — By Air or Sea',
      paragraphs: [
        "Not every shipment needs the same speed, so we run door-to-door freight both ways, and the right one depends on what you're sending and when it needs to land.",
      ],
      cards: [
        {
          icon: FaPlane,
          title: 'Door to Door Air Cargo',
          text: "For shipments that can't sit in transit for weeks — documents with a deadline, commercial samples, personal items someone's waiting on — door to door air cargo is the faster route. Pickup and export documentation happen on the same timeline as a normal air freight booking, but instead of the recipient collecting from an airport cargo terminal, we deliver straight to their door once it clears import customs.",
        },
        {
          icon: FaShip,
          title: 'Door to Door Sea Freight',
          text: "For bulkier or less time-sensitive cargo, sea freight keeps the cost down considerably per kilogram, and it's still fully door-to-door — we handle the port-to-door leg on the receiving end so the recipient isn't the one dealing with a customs broker they've never spoken to before.",
        },
      ],
    },
    {
      heading: 'How Our International Door-to-Door Cargo Service Works',
      steps: [
        { title: 'You book a pickup', text: 'Give us the cargo details, origin address, and destination.' },
        { title: 'We collect from your address', text: 'Home, office, or warehouse, no drop-off required.' },
        { title: 'Export documentation is prepared', text: 'We handle the paperwork before it becomes a hold-up at customs.' },
        { title: 'Cargo moves by air or sea', text: 'Depending on the service you choose.' },
        { title: 'Import clearance at the destination', text: "Again, on our side, not the recipient's." },
        { title: 'Final delivery', text: "Straight to the recipient's address, with confirmation sent back to you." },
      ],
    },
    {
      heading: 'What You Can Ship Door to Door',
      paragraphs: [
        'We handle personal belongings, household items, commercial and business shipments, documents, and gifts or parcels for family and friends abroad.',
      ],
      warning: CANNOT_SHIP,
    },
    {
      heading: 'Countries We Cover for International Door-to-Door Cargo Services',
      paragraphs: [
        'We currently run international door-to-door cargo services to India, the Philippines, Bangladesh, Pakistan, Nepal, Sri Lanka, Malaysia, Singapore, Brunei, and the United Kingdom (Malaysia and Brunei by air cargo only). For a country not listed here, contact us directly—we can usually confirm feasibility the same day.',
      ],
      countries: true,
    },
    {
      heading: 'Why Choose Sahari Cargo for Door-to-Door Delivery',
      paragraphs: [
        "Because the alternative is usually three separate companies who don't talk to each other, and a shipment that goes quiet somewhere in the middle. We keep pickup, customs, and delivery under one roof, with tracking that actually updates and a team across our 12 branches in Saudi Arabia who can tell you exactly where your cargo is if you call and ask — not a generic \"in transit\" status that hasn't changed in a week.",
      ],
    },
  ],
  faqs: [
    {
      q: 'What does "door to door" cargo actually include?',
      a: "It covers the full journey — pickup from your address, export customs clearance, international transport by air or sea, import clearance at the destination, and final delivery to the recipient's address. You don't need to arrange any part of it separately.",
    },
    {
      q: 'How long does door-to-door delivery take?',
      a: 'Door-to-door delivery usually takes 10–15 days by air or sea, depending on the destination country. Request a quote for an exact timeline on your specific route.',
    },
    {
      q: 'Is door-to-door air cargo more expensive than sea freight?',
      a: 'Generally yes, air cargo costs more per kilogram because of speed, but for smaller or urgent shipments the total cost difference is often smaller than people expect. We can quote both options so you can compare directly.',
    },
    {
      q: 'Do I need to handle customs paperwork myself?',
      a: "No. Export documentation from Saudi Arabia and import clearance at the destination are both handled as part of the service. You'll need to provide accurate details about the cargo contents, but the clearance process itself is ours to manage.",
    },
    {
      q: 'Can I track my door-to-door shipment?',
      a: 'Yes, every shipment includes tracking from pickup through to delivery, so you and the recipient can both see where it is at each stage rather than waiting on updates.',
    },
    {
      q: 'Which countries do you offer door-to-door cargo delivery to?',
      a: "We currently cover India, the Philippines, Bangladesh, Pakistan, Nepal, Sri Lanka, Malaysia, Singapore, Brunei, and the United Kingdom (Malaysia and Brunei by air cargo only). If your destination isn't listed, get in touch—we regularly add routes and can often confirm the same day whether we can service a new destination.",
    },
  ],
  cta: {
    title: 'Ready to ship door to door?',
    text: "Tell us what you're sending and where — we'll quote air and sea so you can compare.",
  },
};

export default doorToDoor;
